import axios from 'axios'
import { getAiToolApiConfig } from './apiConfig'
import { resolvePricingDisplay } from './pricingTypes'

const CURRENCY_SYMBOLS = {
  INR: '₹',
  USD: '$',
}

let cachedPricing = null
let fetchPromise = null

export async function fetchAiToolPricing({ force = false } = {}) {
  if (!force && cachedPricing) {
    return cachedPricing
  }

  if (!force && fetchPromise) {
    return fetchPromise
  }

  fetchPromise = axios
    .get(
      `${process.env.REACT_APP_API_URL}/ai-tool/pricing`,
      getAiToolApiConfig(),
    )
    .then((response) => {
      const data = Array.isArray(response.data?.data) ? response.data.data : []
      cachedPricing = data
      return data
    })
    .catch((error) => {
      fetchPromise = null
      throw error
    })

  return fetchPromise
}

export function getCachedAiToolPricing() {
  return cachedPricing || []
}

export function buildPricingMap(pricingList = []) {
  return pricingList.reduce((acc, item) => {
    if (item?.type) {
      acc[item.type] = item
    }
    return acc
  }, {})
}

export function getCurrencySymbol(currency = 'INR') {
  return CURRENCY_SYMBOLS[currency] || `${currency} `
}

export function formatPricingEntry(entry, { prefix = '' } = {}) {
  if (!entry || entry.price == null) {
    return ''
  }

  return `${prefix}${getCurrencySymbol(entry.currency)}${entry.price}`
}

export function formatPricingType(
  pricingMap,
  pricingType,
  { prefix = '', suffix = 'per report', showSuffix = true } = {},
) {
  const formatted = formatPricingEntry(pricingMap?.[pricingType], { prefix })
  if (!formatted) {
    return ''
  }

  return showSuffix && suffix ? `${formatted} ${suffix}` : formatted
}

export function getLowestPricingEntry(pricingList = []) {
  if (!pricingList.length) {
    return null
  }

  return pricingList.reduce((lowest, item) => {
    if (item?.price == null) {
      return lowest
    }

    if (!lowest || item.price < lowest.price) {
      return item
    }

    return lowest
  }, null)
}

export function formatLowestAiToolPrice(
  pricingList = [],
  { prefix = 'Starting From ' } = {},
) {
  const lowest = getLowestPricingEntry(pricingList)
  return formatPricingEntry(lowest, { prefix })
}

export function getPricingLabel(
  pricingMap,
  {
    pricingType,
    toolType,
    mode,
    suffix = 'per report',
    showSuffix = true,
  } = {},
) {
  const resolved = resolvePricingDisplay({ pricingType, toolType, mode })

  if (!resolved.pricingType) {
    return ''
  }

  return formatPricingType(pricingMap, resolved.pricingType, {
    prefix: resolved.pricePrefix,
    suffix,
    showSuffix,
  })
}

export function getPricingAmount(
  pricingMap,
  { pricingType, toolType, mode } = {},
) {
  const resolved = resolvePricingDisplay({ pricingType, toolType, mode })
  const entry = pricingMap?.[resolved.pricingType]
  if (!entry || entry.price == null) {
    return ''
  }

  return formatPricingEntry(entry, { prefix: resolved.pricePrefix })
}
